import { action, computed, config, debug, helpers, NetbankingAPI, UserManagementAPI } from "aaa";
import { SupportedLocales } from "config";
import { Action, Location } from "history";
import * as _ from "lodash";
import { isArray } from "lodash";
import { observable } from "mobx";
import { GeorgeBusinessAPIClient, LoginAPIClient, NetbankingAPIClient } from "network/APIClient";
import { urljoin } from "network/NetworkStapler";
import { parse, stringify } from "query-string";
import { ISettingsGeorgeBusinessResponse, ISettingsResponse } from "types/settings";
import { gba } from "util/georgeBusinessAdapter";
import { safeWindowOpen } from "util/helpers";
import authStore from "./AuthStore";
import { debugStore } from "./DebugStore";
import { pushRoute, routerData } from "./RouterFunctions";
import { history } from "./RouterStore";
import trackingStore from "./TrackingStore";

export type ExitError =
    "unauthorized" |
    "invalidCardId" |
    "unknown" |
    "userCanceled" |
    "pinResetFailed" |
    "unauthorized" |
    "forbidden" |
    "consumerLoanFailed" |
    "shopCreditCardFailed" |
    "consumerLoanRejected"
    ;

export type ExitSuccess =
    "pinReset" |
    "consumerLoan" |
    "creditCardPurchase" |
    "prepaid" |
    "bankCard" |
    "changeRepayment" |

    "quickDrawdown" |
    "geoControl" |
    "optioPay" |
    "optioPayActivation" |
    "deferralService" |
    "success" |
    "showCardData"
    ;

/**
 * Parse access token from e.g. george login
 */
function checkForErsteAccessParams(pathname: string) {
    let ret;
    const ERSTE_TOKEN_ID = "/access_token";
    if (_.startsWith(pathname, ERSTE_TOKEN_ID)) {
        const externalQueryParams = parse(pathname);
        // debug.log("### pathname", pathname);
        // debug.log("### externalQueryParams", externalQueryParams);

        ret = {
            token: externalQueryParams[ERSTE_TOKEN_ID] as string
        };

        debug.log("### received Erste access params", ret);
    }

    return ret;
}

export type IDeeplinkState = {
    srcClientId?: string;
    redirectUri?: string;
    returnState?: string;
};

// useds to communicate with external environment (native app container)
// listen to url changes and picks query parameters
export class ExternalInterfaceStore {
    @observable isRehydrated = false;

    @observable contactId?: string;

    @observable currentCardId?: string;
    @observable invitationId?: string;

    @observable accountId?: string;

    @observable productId?: string;
    @observable conditionsGroupId?: string;
    @observable channelProduct?: string;
    @observable upgradeProduct?: string;

    // used by survey
    @observable urlDecoded?: string;
    @observable expiryDate?: string;

    @observable productCode?: string;

    @observable clientId?: "georgeclient" | "transactionapp" | "store";

    @observable channel?: "09" | "10" | "27" | "28"; // HYB-83, 09=georgeclient, 10=transactionapp, 27=George business web, 28=George business app

    @computed
    get isGeorgeBusiness() {
        return this.channel === "27" || this.channel === "28" || window.location.hostname.startsWith("george-business.");
    }

    // We are embedded in a native iOS or Android app
    @computed
    get isGeorgeApp() {
        return this.clientId === "transactionapp"
            || this.channel === "10"
            || this.channel === "28";
    }

    // We were called from the george web application
    @computed
    get isGeorgeWeb() {
        return this.clientId === "georgeclient"
            || this.channel === "09"
            || this.channel === "27";
    }

    georgeDeepLinkState?: IDeeplinkState;

    @observable useragent?: "android" | "ios" | "windows" | "mac" | "harmonyos";

    @observable successURL?: string;
    @observable errorURL?: string;

    // Appointment parameters
    @observable topicId?: string;

    // Consumer loan parameters
    @observable amount?: number;
    @observable duration?: number;

    // Store category anchor
    @observable category?: string;

    // Optiopay parameters
    @observable autoforward?: boolean;

    // DeferralService parameters 
    // TODO remove if unnecessary in future
    @observable renew?: boolean;

    @observable origin?: string;
    @observable section?: string;
    @observable leadId?: string;
    @observable handoverUrl?: string;

    // Pension Funds
    @observable contractId?: string;

    @observable isRevoking = false;
    // If revokeOnExit = true then revoke the auth token before leaving the app
    revokeOnExit = false;

    @observable isLoggingOut = false;
    @observable isExchangingToken = false;

    // Settings MUST be used from ExternalInterfaceStore and should not be part of
    // a feature store like SidentityStore because settings are also used by the AppContainer for locale
    @observable settings?: ISettingsResponse | ISettingsGeorgeBusinessResponse;

    // Controls the locale from the settings call. The app locale is authStore.locale. Why is it that way?
    // It's complicated. All started with authStore.locale, then locale from query param. Later clientID = georgelient was
    // added, which did not pass query params. So locale had to be retrieved from settings call. This resulted in settings
    // being called twice, which s-IT does not want because of performance. So now this flag is used to stagger settings
    // calls in AppContainer.tsx
    // IMPORTANT: THIS MUST NOT BE AN OBSERVABLE -> otherwise it'll trigger an unnecessary rerender.
    // On the other hand settings should be observable for Features that rely on loading settings in their Container load
    @observable settingsLocale?: SupportedLocales;

    @observable isLoadingSettings = false;

    @observable queryParsed = false;
    querySubscribed = false;

    private applyPostQueryParsingRules(pathname: string) {
        if (pathname === "/sidentity" || pathname.startsWith("/sidentity/")) {
            // Default to georgeclient for sidentity (confirmed by mail on 2019-07-25)
            if (!this.clientId) {
                this.clientId = "georgeclient";
            }
        }
    }

    @computed get isIos(): boolean {
        return this.useragent === "ios" || (!this.useragent && helpers.isIos());
    }

    @computed get isAndroid(): boolean {
        return this.useragent === "android" || (!this.useragent && helpers.isAndroid());
    }

    @computed get isWindows(): boolean {
        return this.useragent === "windows" || (!this.useragent && helpers.isWindows());
    }

    @computed get isMac(): boolean {
        return this.useragent === "mac" || (!this.useragent && helpers.isMac());
    }

    @computed get isHuawei(): boolean {
        return this.useragent === "harmonyos" || (!this.useragent && helpers.isHuawei());
    }

    @computed get iosStoreIconUrl(): string {
        if (authStore.locale === "de") {
            return require("../assets/AppStore_Deutsch.svg");
        } else {
            return require("../assets/AppStore_English.svg");
        }
    }

    @computed get androidStoreIconUrl(): string {
        if (authStore.locale === "de") {
            return require("../assets/google-play-badge_de.png");
        } else {
            return require("../assets/google-play-badge_en.png");
        }
    }

    @computed get appGalleryIconUrl(): string {
        if (authStore.locale === "de") {
            return require("../assets/AppGallery_Badge_de.png");
        } else {
            return require("../assets/AppGallery_Badge_en.png");
        }
    }

    subscribeToHistory() {
        if (this.querySubscribed) {
            return;
        }

        // listens to changes in the history before hashbang
        history.subscribe((location: Location, _action: Action) => {
            const queryParams = parse(location.search);
            const hashParams = parse(location.hash);

            debug.log(`### ExternalInterfaceStore ${location.pathname} hash = ${location.hash} queryParam =`, queryParams);

            // Store for query forwarding, but without access token
            routerData.queryParams = { ...queryParams };
            const hasAccessToken = routerData.queryParams.accessToken || routerData.queryParams.access_token;
            const hasHashAccessToken = hashParams.accessToken || hashParams.access_token;

            if (hasAccessToken) {
                delete routerData.queryParams.accessToken;
                delete routerData.queryParams.access_token;
            }

            this.setCardId(queryParams);
            this.setContactId(queryParams);
            this.setProductIds(queryParams);
            this.setExternalInterfaceUrls(queryParams);
            this.setAccessibilityLocale();
            this.setClientId(queryParams);
            this.setUserAgent(queryParams);
            this.setDebug(queryParams);
            this.setAmount(queryParams);
            this.setDuration(queryParams);
            this.setChannel(queryParams);
            this.setAutoforward(queryParams);
            this.setRenew(queryParams);
            this.setSection(queryParams);
            this.setLeadId(queryParams);
            this.setTopicId(queryParams);
            this.setOrigin(queryParams);
            this.setHandoverUrl(queryParams);
            this.setContractId(queryParams);
            this.setCategory(queryParams);
            this.setUpgradeProduct(queryParams);
            this.setAccountId(queryParams);
            this.setUrlDecoded(queryParams);
            this.setExpiryDate(queryParams);
            this.setInvitationId(queryParams);

            if (this.isGeorgeApp) {
                this.setLocale(queryParams);
            }

            this.applyPostQueryParsingRules(location.pathname);

            const redirect = this.setAccessToken(queryParams, hashParams, location);
            if (redirect) {
                // sparkasse login passes target path via query params, because hash is cut off by their login
                const passedQuery = window.location.search;
                let redirectUrl = "";
                if (passedQuery && passedQuery[0] === "?") {
                    redirectUrl = passedQuery.slice(1);
                }

                pushRoute("/" + redirectUrl);
            } else {
                if (hasAccessToken || hasHashAccessToken) {
                    this.removeAccessTokenFromUrl(location, queryParams);
                }
            }

            debug.log("### ExternalInterfaceStore parsed query params");
            this.queryParsed = true;
        });

        this.querySubscribed = true;
    }

    private setLocaleFromSettings() {
        switch (this.settings?.language) {
            case "DE":
            case "de":
                this.settingsLocale = "de";
                break;
            case "EN":
            case "en":
                this.settingsLocale = "en";
                break;
            default:
                this.settingsLocale = "en";
                break;
        }
    }

    @action async loadSettingsLocale() {
        if (!this.settingsLocale) {
            try {
                await this.loadSettings();
            } catch (err) {
                throw err;
            } finally {
                this.setLocaleFromSettings();
            }
        }
    }

    @action async loadSettings() {
        const isGeorgeBusiness = window.location.hostname.startsWith("george-business.");
        this.isLoadingSettings = true;

        try {
            this.settings = await gba.getSettings();
            debug.log(isGeorgeBusiness ? "### george business settings" : "### settings", this.settings);
        } catch (error) {
            debug.log(isGeorgeBusiness ? "### Error loading george business settings" : "### Error loading settings");
            throw error;
        } finally {
            this.isLoadingSettings = false;
        }
    }

    get georgeBaseUrl() {
        let baseUrl = "";
        if (!config.network.environmentIsFat) {
            baseUrl = this.isGeorgeBusiness
                ? "https://george-business.sparkasse.at"
                : "https://george.sparkasse.at";
        } else {
            let env = config.network.environment;
            if (env === "f1") {
                // See email from Thomas Motal. fat1 maps to fat, fat2, fat3 stay the same.
                env = "fat";
            } else {
                env = env.replace("f", "fat");
            }
            baseUrl = this.isGeorgeBusiness
                ? `https://george-business.${env}.sparkasse.at`
                : `https://george.${env}.sparkasse.at`;
        }

        return baseUrl;
    }

    get federatedLoginUrl() {
        const token = authStore.accessToken;

        // If present -> append deep links into George WebApp (NOT George native)
        let state = "";
        if (this.georgeDeepLinkState?.returnState) {
            state = `&state=${encodeURIComponent(this.georgeDeepLinkState.returnState)}`;
        } else if (this.section) {
            state = `&${encodeURIComponent(this.section)}`;
        }

        let baseUrl = this.georgeBaseUrl;

        if (this.georgeDeepLinkState?.redirectUri) {
            baseUrl = this.georgeDeepLinkState.redirectUri;
        }

        const ret = `${baseUrl}/index.html#access_token=${token}${state}`;

        debug.log("### federated login", ret);
        return ret;
    }

    /**
     * For security reasons remove visible accessToken
     * @param queryParams
     */
    private removeAccessTokenFromUrl(location: Location, queryParams: any) {
        if (authStore.authorized) {
            const url = location.pathname;
            debug.log("### removed access token, redirect to", url);
            pushRoute(url);
        }
    }

    /**
     * Set access token, returns true if we want to redirect afterwards
     */
    private setAccessToken = (queryParams: any, hashParams: any, location: { pathname: string, hash: string }): boolean => {
        // set access token
        let accessToken = queryParams.accessToken || queryParams.access_token;
        if (accessToken) {
            debug.log("### access token parsed from query", accessToken);
        } else {
            // look for hash access token -> georgeclient passes access token this way
            const hashAccessToken = hashParams.accessToken || hashParams.access_token;

            if (hashAccessToken) {
                accessToken = hashAccessToken;
                debug.log("### access token parsed from hash", accessToken);
            }
        }

        if (accessToken) {
            authStore.accessToken = accessToken;
        } else {
            const ersteAccessParams = checkForErsteAccessParams(location.pathname);
            if (ersteAccessParams
                && ersteAccessParams.token
            ) {
                authStore.accessToken = ersteAccessParams.token;
                debug.log("### access token parsed from erste login", accessToken);
                return true;
            }
        }

        // get base64 deeplinkstate from either query params or hash params
        let base64State = queryParams.state ?? hashParams.state;

        if (base64State) {
            try {
                const state = JSON.parse(atob(base64State)) as IDeeplinkState;
                this.georgeDeepLinkState = state;

                debug.log("### parsed deeplink state:", state);
            } catch (error) {
                debug.log("### parsing deeplink state failed");
            }
        }
        return false;
    }

    private setCardId = (queryParams: any) => {
        // find current cardId
        if (queryParams.cardId) {
            this.currentCardId = queryParams.cardId;
        }
    }

    private setInvitationId = (queryParams: any) => {
        if (queryParams.id) {
            // TODO: Param name has to be clarified
            this.invitationId = queryParams.id;
        }
    }

    private setAccountId = (queryParams: any) => {
        // find current accountId
        if (queryParams.accountId) {
            this.accountId = queryParams.accountId;
        }
    }

    private setCategory = (queryParams: any) => {
        // find current category
        if (queryParams.category) {
            this.category = decodeURIComponent(queryParams.category);
        }
    }

    private setContactId = (queryParams: any) => {
        // Needed for appointments
        if (queryParams.contactId || queryParams.contactID) {
            this.contactId = queryParams.contactId || queryParams.contactID;
        }
    }

    private setProductIds = (queryParams: any) => {
        if (queryParams.productId || queryParams.productID) {
            this.productId = queryParams.productId || queryParams.productID;
            debug.log("### ExternalInterfaceStore productId", this.productId);
        }
        if (queryParams.conditionsGroupId) {
            this.conditionsGroupId = queryParams.conditionsGroupId;
            debug.log("### ExternalInterfaceStore conditionsGroupId", this.conditionsGroupId);
        }
        if (queryParams.channelProduct) {
            this.channelProduct = queryParams.channelProduct;
            debug.log("### ExternalInterfaceStore channelProduct", this.channelProduct);
        }
        if (queryParams.productCode) {
            this.productCode = queryParams.productCode;
            debug.log("### ExternalInterfaceStore productCode", this.productCode);
        }
    }

    private setClientId = (queryParams: any) => {
        if (queryParams.clientId || queryParams.clientID || queryParams.client_id) {
            this.clientId = queryParams.clientId || queryParams.clientID || queryParams.client_id;
            debug.log("### parsed clientId", this.clientId);

            // HYB-78 -> exit should revoke token before leaving
            if (this.clientId === "store") {
                this.revokeOnExit = true;
            }
        }
    }

    private setChannel = (queryParams: any) => {
        if (queryParams.channel) {
            this.channel = queryParams.channel;
            debug.log("### parsed channel", this.channel);
        }
    }

    private setUserAgent = (queryParams: any) => {
        if (queryParams.useragent) {
            this.useragent = queryParams.useragent;
        }
    }

    private setDebug = (queryParams: any) => {
        if (queryParams.debug) {
            debugStore.debug = queryParams.debug === "true" || queryParams.debug === "1";
        }
    }

    private setLocale = (queryParams: any) => {
        // set locale
        if (queryParams.locale) {
            // in rare cases locale can come uppercase and multiple times
            // then use the first locale in the array and make it case insensitive
            let locale = isArray(queryParams.locale)
                ? queryParams.locale[0]
                : queryParams.locale;

            locale = typeof locale === "string" ? locale.toLocaleLowerCase() : locale;

            if (locale === "de" ||
                locale === "en"
            ) {
                authStore.locale = locale;
            }

            switch (locale) {
                // ALWAYS use "de" locale for currency formatting
                // case "en":
                //     config.CURRENCY_DECIMAL_SEPARATOR = ".";
                //     config.CURRENCY_THOUSANDS_SEPARATOR = ",";
                //     break;
                default:
                    config.CURRENCY_DECIMAL_SEPARATOR = ",";
                    config.CURRENCY_THOUSANDS_SEPARATOR = ".";
                    break;
            }
        }
    }

    /**
     * Must be called after setLocale()
     */
    private setAccessibilityLocale() {
        document.getElementsByTagName("html")[0].setAttribute("lang", authStore.locale);
    }

    private setExternalInterfaceUrls = (queryParams: any) => {
        const successURL = queryParams.successURL || queryParams.successUrl || queryParams.successurl;
        if (successURL) {
            this.successURL = successURL;
        }

        const errorURL = queryParams.errorURL || queryParams.errorUrl || queryParams.errorurl;
        if (errorURL) {
            this.errorURL = errorURL;
        }

        debug.log("### external URLs " + this.successURL + " " + this.errorURL);
    }

    private setAmount = (queryParams: any) => {
        if (queryParams.amount) {
            this.amount = parseInt(queryParams.amount, 10);
            debug.log("### parsed amount", this.amount);
        }
    }

    private setDuration = (queryParams: any) => {
        if (queryParams.duration) {
            this.duration = parseInt(queryParams.duration, 10);
            debug.log("### parsed duration", this.duration);
        }
    }

    private setExpiryDate = (queryParams: any) => {
        if (queryParams.expiryDate) {
            this.expiryDate = queryParams.expiryDate;
            debug.log("### parsed expiryDate", this.expiryDate);
        }
    }

    private setAutoforward = (queryParams: any) => {
        if (queryParams.autoforward) {
            this.autoforward = queryParams.autoforward === "true";
            debug.log("### parsed autoforward", this.autoforward);
        }
    }

    private setRenew = (queryParams: any) => {
        if (queryParams.renew) {
            this.renew = queryParams.renew === "true";
            debug.log("### parsed renew", this.renew);
        }
    }

    private setSection = (queryParams: any) => {
        if (queryParams.section) {
            this.section = queryParams.section;
            debug.log("### parsed section", this.section);
        }
    }

    private setLeadId = (queryParams: any) => {
        if (queryParams.leadId) {
            this.leadId = queryParams.leadId;
            debug.log("### parsed leadId", this.leadId);
        }
    }

    private setTopicId = (queryParams: any) => {
        if (queryParams.topicId) {
            this.topicId = queryParams.topicId;
            debug.log("### parsed topicId", this.topicId);
        }
    }

    private setOrigin = (queryParams: any) => {
        if (queryParams.origin) {
            this.origin = queryParams.origin;
            debug.log("### parsed origin", this.origin);
        }
    }

    private setHandoverUrl = (queryParams: any) => {
        if (queryParams.handoverUrl) {
            this.handoverUrl = queryParams.handoverUrl;
            debug.log("### parsed handoverUrl", this.handoverUrl);
        }
    }

    private setContractId = (queryParams: any) => {
        if (queryParams.contractId || queryParams.contractID) {
            this.contractId = queryParams.contractId || queryParams.contractID;
            debug.log("### parsed contractId", this.contractId);
        }
    }

    private setUpgradeProduct = (queryParams: any) => {
        if (queryParams.upgradeProduct) {
            this.upgradeProduct = queryParams.upgradeProduct;
            debug.log("### parsed upgradeProduct", this.upgradeProduct);
        }
    }

    private setUrlDecoded = (queryParams: any) => {
        if (queryParams.urlDecoded) {
            this.urlDecoded = decodeURIComponent(queryParams.urlDecoded);
            debug.log("### parsed urlDecoded", this.urlDecoded);
        }
    }

    // Adds error and error reason to url in form of exitError={ExitError}
    // The native container has to listen for this
    public raiseExitError = async (reason: ExitError = "unknown") => {
        debug.log("### raiseExitError", reason);

        if (this.revokeOnExit) {
            await this.revokeToken();
        }

        if (this.errorURL) {
            debug.log("### calling errorURL " + this.errorURL);
            safeWindowOpen(this.errorURL);
        } else if (this.isGeorgeWeb) {
            if (!this.revokeOnExit && !history.location.pathname.startsWith("/sidentity")) {
                await this.tokenExchange();
            }
            debug.log("### error go to george web");
            safeWindowOpen(this.federatedLoginUrl);
        } else {
            debug.log("### error via queryParam");
            const params = parse(history.location.search);
            params.exitError = reason;
            const query = "?" + stringify(params);
            // safeWindowOpen("#" + history.location.pathname + query);
            safeWindowOpen(urljoin(config.network.appBaseUrl, history.location.pathname) + query);
        }
    }

    // Adds success and successreason to url in form of exitSuccess={ExitSuccess}
    // The native container has to listen for this
    public raiseExitSuccess = async (reason: ExitSuccess = "success") => {
        debug.log("### raiseExitSuccess", reason);

        if (this.revokeOnExit) {
            await this.revokeToken();
        }

        if (this.successURL) {
            debug.log("### calling successURL", this.successURL);
            safeWindowOpen(this.successURL);
        } else if (this.isGeorgeWeb) {
            if (!this.revokeOnExit && !history.location.pathname.startsWith("/sidentity")) {
                await this.tokenExchange();
            }
            debug.log("### success go to george web");
            safeWindowOpen(this.federatedLoginUrl);
        } else {
            debug.log("### success via queryParam");
            const params = parse(history.location.search);
            params.exitSuccess = reason;
            const query = "?" + stringify(params);
            // safeWindowOpen("#" + history.location.pathname + query);
            safeWindowOpen(urljoin(config.network.appBaseUrl, history.location.pathname) + query);
        }
    }

    revokeToken = async () => {
        if (!authStore.accessToken) {
            return;
        }

        try {
            this.isRevoking = true;
            const res = await LoginAPIClient.request(UserManagementAPI.revokeToken());
            debug.log("### revoke", res.body);
        } catch (error) {
            debug.log("### revoke failed", error);
            pushRoute("/error");
        } finally {
            this.isRevoking = false;
        }
    }

    logout = async () => {
        try {
            this.isLoggingOut = true;
            const res = await LoginAPIClient.request(UserManagementAPI.logout());
            authStore.logout();
            debug.log("### logout", res.body);
        } catch (error) {
            debug.log("### logout failed", error);
            pushRoute("/error");
        } finally {
            this.isLoggingOut = false;
        }
    }

    logoutWebHeader = async () => {
        await trackingStore.trackEvent({ eventName: "loggedOut" });

        await this.logout();
        // HYB-83: We do not redirect to logout confirmation (which would open on redirect to getGeorgeUrl()) but to
        // sparkasse landing page instead
        safeWindowOpen(
            this.isGeorgeBusiness
                ? "https://www.sparkasse.at/erstebank/unternehmen/wiedersehen-george-business"
                : "https://www.sparkasse.at/erstebank/wiedersehen-george"
        );
    }

    ////////////////////////////////////////////////////////////////
    // ATTENTION: This call only works with clientID "INT82482".
    // But we cannot get an access token with that ID, so the feature
    // is only testable from within Erste Bank. Confirmed to work
    // by Mladen on 2020-03-25 (during Corona quarantine).
    ////////////////////////////////////////////////////////////////
    tokenExchange = async () => {
        try {
            this.isExchangingToken = true;
            // Make sure you are using clientID "INT82482" during testing otherwise token exchange will fail
            const businessTargetClientId = this.georgeDeepLinkState?.srcClientId ?? this.clientId ?? "INTGB5J7";
            const res = this.isGeorgeBusiness
                ? await GeorgeBusinessAPIClient.requestType(NetbankingAPI.tokenExchange(businessTargetClientId, true)) // TODO - which client ID should be used and how will it be available here?
                : await NetbankingAPIClient.requestType(NetbankingAPI.tokenExchange());
            authStore.accessToken = res.access_token;
            debug.log("### token exchange", res);
        } catch (error) {
            debug.log("### token exchange failed", error);
            pushRoute("/error");
        } finally {
            this.isExchangingToken = false;
        }
    }
}

const externalInterfaceStore = new ExternalInterfaceStore();
export default externalInterfaceStore;



// WEBPACK FOOTER //
// ./src/stores/ExternalInterfaceStore.ts